import { useRef, useState } from 'react';

import classNames from 'classnames';

import { useTrackEvent } from '@/lib/analytics';
import { getExplorationCellIndex } from '@/core/exploration';
import { getCell } from '@/core/cell';
import {
  canDropOnRow,
  CellDragKey,
  getRowIdKey,
  isCellDragEvent,
  trackDropEvent,
  updateCellRowCollapsed,
  updateCellRowHeight,
} from '@/core/exploration';

import { useExplorationContext } from '../exploration-context';
import { CellResizeArea } from './cell-resize-area';
import { useExplorationCellContext } from '../exploration-cell-context';

import styles from './exploration-layout.module.scss';

const MinCellHeight = 60;
const MaxCellHeight = 5000;

interface DraggableCellProps {
  cellId: string;
  isDraggable: boolean;
  isResizable?: boolean;
  height?: number;
  heightOverride?: number;
  selected?: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
  setRowHeightOverride?: (height: number | undefined) => void;
}

export const DraggableCell = (props: DraggableCellProps) => {
  const {
    isDraggable,
    isResizable = true,
    selected = false,
    cellId,
    height,
    setRowHeightOverride,
    heightOverride,
  } = props;
  const [isDragged, setIsDragged] = useState(false);
  const [isResized, setIsResized] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragStartHeight, setDragStartHeight] = useState<number | undefined>(undefined);
  const [side, setSide] = useState<'left' | 'right'>('left');
  const elementRef = useRef<HTMLDivElement>(null);

  const { exploration, moveCell, setExploration } = useExplorationContext();
  const { toggleCollapsed, isCollapsed, isConversationPart, isConversationSubject } =
    useExplorationCellContext();
  const trackEvent = useTrackEvent();

  const cell = getCell(exploration.view.cells, cellId);

  if (cell === undefined) {
    return props.children;
  }

  const appliedHeight = isCollapsed ? undefined : (heightOverride ?? height);

  const heightValue =
    appliedHeight === undefined ? (isResized ? dragStartHeight : 'auto') : `${appliedHeight}px`;

  const containerStyles = {
    height: heightValue,
    maxHeight: isCollapsed || appliedHeight !== undefined ? heightValue : 'none',
    ...props.style,
  };

  const setCellHeight = (newHeight: number | undefined) => {
    setExploration(
      newHeight === MinCellHeight
        ? updateCellRowCollapsed(
            updateCellRowHeight(exploration, cell.id, undefined),
            cell.id,
            true,
          )
        : updateCellRowHeight(exploration, cell.id, newHeight),
    );
  };

  return (
    <div
      style={containerStyles}
      className={classNames(styles.draggableCell, {
        [styles.isDragOver]: isDragOver,
        [styles.isResized]: isResized,
        [styles.droppableLeft]: isDragOver && !isDragged && side === 'left',
        [styles.droppableRight]: isDragOver && !isDragged && side === 'right',
        [styles.conversationSubjectCell]: isConversationSubject,
      })}
      data-id={cellId}
      ref={elementRef}
      draggable={isDraggable}
      onDragStart={(event) => {
        event.stopPropagation(); // Allow nested draggables
        event.dataTransfer.clearData();
        event.dataTransfer.setData(CellDragKey, cell.id.toString());
        event.dataTransfer.setData(getRowIdKey(cell), 'true');
        event.dataTransfer.effectAllowed = 'all';
        setIsDragged(true);
      }}
      onDragEnd={() => {
        setIsDragOver(false);
        setIsDragged(false);
      }}
      onDragOver={(event) => {
        if (
          event.dataTransfer.types.includes('Files') ||
          event.dataTransfer.types.includes('files')
        ) {
          return true;
        }
        if (!isCellDragEvent(event) || !canDropOnRow(exploration, cell, event)) {
          return;
        }
        event.dataTransfer.dropEffect = 'move';
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const xPos = (event.clientX - rect.left) / rect.width;
        const currentSide = xPos > 0.5 ? 'right' : 'left';
        if (currentSide !== side) {
          setSide(currentSide);
        }
      }}
      onDragEnter={(event) => {
        if (!isCellDragEvent(event) || !canDropOnRow(exploration, cell, event)) {
          return;
        }
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={(event) => {
        if (
          elementRef.current &&
          (!elementRef.current.contains(event.target as Node) ||
            elementRef.current === event.target)
        ) {
          setIsDragOver(false);
        }
      }}
      onDrop={(event) => {
        if (!isCellDragEvent(event) || !canDropOnRow(exploration, cell, event)) {
          return;
        }
        setIsDragOver(false);
        setIsDragged(false);
        const droppedCellId = event.dataTransfer.getData(CellDragKey);
        const droppedCellIndex = getExplorationCellIndex(exploration, droppedCellId);
        if (droppedCellId === cell.id || droppedCellIndex === -1) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        moveCell(droppedCellId, cell.id, side === 'right', true, elementRef.current?.clientHeight);
        trackDropEvent(
          exploration,
          droppedCellIndex,
          getExplorationCellIndex(exploration, cell.id),
          getCell(exploration.view.cells, droppedCellId),
          side === 'right',
          true,
          trackEvent,
        );
      }}>
      {props.children}
      {isResizable && (
        <CellResizeArea
          isCellSelected={selected}
          isCellBordered={isConversationPart}
          onResizeStart={() => {
            const currentHeight = elementRef.current?.clientHeight ?? undefined;
            setIsResized(true);
            setDragStartHeight(currentHeight);
            if (isCollapsed) {
              toggleCollapsed();
            }
          }}
          onResize={(dy) => {
            setRowHeightOverride?.(
              Math.max(MinCellHeight, Math.min(MaxCellHeight, (dragStartHeight ?? 500) + dy)),
            );
          }}
          onResizeEnd={() => {
            setIsResized(false);
            if (elementRef.current === null || heightOverride === undefined) {
              return;
            }
            height !== heightOverride && setCellHeight(heightOverride);
            setRowHeightOverride?.(undefined);
          }}
          onResetSize={() => {
            height !== undefined && setCellHeight(undefined);
          }}
        />
      )}
    </div>
  );
};
